import { useState } from "react";
import { motion } from "framer-motion";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { BUDGET_RANGES, CONTACT_EMAIL } from "../lib/constants";
import { useI18n } from "../context/I18nContext";

const inputClass =
  "w-full bg-transparent border-b border-white/20 focus:border-gold text-white placeholder-white/30 py-4 font-sans text-sm outline-none transition-colors duration-300";

export const Contact = () => {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", search_type: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const types = t("opt.searchTypes");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t("toast.nameEmail"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/requests", { ...form, kind: "contact" });
      toast.success(t("toast.contactOk"));
      setForm({ name: "", email: "", phone: "", search_type: "", budget: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="bg-navy grain relative py-24 md:py-36 px-6 lg:px-10 overflow-hidden">
      <div className="max-w-[1200px] mx-auto relative z-10 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <div>
          <p className="font-sans text-gold text-[11px] tracking-luxury uppercase mb-6">{t("contact.label")}</p>
          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="font-serif text-white text-4xl md:text-6xl leading-[1.05]">
            {t("contact.hA")} <span className="italic text-gold">{t("contact.hB")}</span>
          </motion.h2>
          <p className="font-sans text-white/50 text-sm md:text-base mt-8 max-w-md leading-relaxed">{t("contact.body")}</p>
          <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link" className="inline-flex items-center gap-2 mt-8 font-sans text-gold text-sm md:text-base hover:text-gold-light transition-colors">
            <Mail className="w-4 h-4" /> {CONTACT_EMAIL}
          </a>
        </div>

        <motion.form initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }} onSubmit={submit} data-testid="contact-form" className="space-y-6">
          <input data-testid="contact-name" className={inputClass} placeholder={t("f.name")} value={form.name} onChange={update("name")} />
          <div className="grid md:grid-cols-2 gap-6">
            <input data-testid="contact-email" type="email" className={inputClass} placeholder={t("f.email")} value={form.email} onChange={update("email")} />
            <input data-testid="contact-phone" className={inputClass} placeholder={t("f.phone")} value={form.phone} onChange={update("phone")} />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <select data-testid="contact-type" className={`${inputClass} text-white/70`} value={form.search_type} onChange={update("search_type")}>
              <option value="" className="bg-navy">{t("sel.searchType")}</option>
              {(Array.isArray(types) ? types : []).map((tp) => <option key={tp} value={tp} className="bg-navy">{tp}</option>)}
            </select>
            <select data-testid="contact-budget" className={`${inputClass} text-white/70`} value={form.budget} onChange={update("budget")}>
              <option value="" className="bg-navy">{t("sel.budget")}</option>
              {BUDGET_RANGES.map((b) => <option key={b} value={b} className="bg-navy">{b}</option>)}
            </select>
          </div>
          <textarea data-testid="contact-message" rows={3} className={inputClass} placeholder={t("seller.ph.message")} value={form.message} onChange={update("message")} />
          <button type="submit" disabled={loading} data-testid="contact-submit" className="bg-gold text-navy px-10 py-4 rounded-md font-sans text-[11px] tracking-wide-lux uppercase hover:bg-gold-light transition-colors disabled:opacity-60">
            {loading ? t("contact.sending") : t("contact.submit")}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
